import { useCallback } from 'react';
import useTripStore from '../store/useTripStore';
import { useDay } from './useTripStoreSelectors';

type TripDataFieldTuple<T> = [
  { value: T },
  null,
  { setValue: (value: T) => void },
];

/**
 * Experience for a specific day. Abstracts the normalized data structure
 * where experiences live in a separate map and days only carry an
 * experienceId. Reads/writes the shared store directly (Phase 3).
 */
export function useExperienceField(dayIndex: number): TripDataFieldTuple<any> {
  const day = useDay(dayIndex);
  const experiences = useTripStore((state) => state.experiences);
  const updateDay = useTripStore((state) => state.updateDay);
  const setExperience = useTripStore((state) => state.setExperience);

  const experienceId = day?.experienceId;
  const experienceDayIndex = day?.experienceDayIndex;
  const experiencePricing = day?.experiencePricing;

  const experience = experienceId
    ? {
        ...experiences?.[experienceId],
        dayIndex: experienceDayIndex,
        ...(experiencePricing && { tripPrice: experiencePricing }),
      }
    : null;

  const setValue = useCallback(
    (newExperience: any) => {
      if (newExperience === null || newExperience === undefined) {
        updateDay(dayIndex, {
          experienceId: null,
          experienceDayIndex: null,
          experiencePricing: null,
        });
      } else {
        const { tripPrice, ...experienceData } = newExperience;
        setExperience(newExperience.id, experienceData);
        updateDay(dayIndex, {
          experienceId: newExperience.id,
          experienceDayIndex: newExperience.dayIndex || null,
          ...(tripPrice && { experiencePricing: tripPrice }),
        });
      }
    },
    [dayIndex, setExperience, updateDay],
  );

  return [{ value: experience }, null, { setValue }];
}

/**
 * A nested field on the day's experience (e.g. `selectedExtras`,
 * `priceLoading`). `selectedExtras`/`priceLoading` are day-specific (stored
 * on the day itself, not the experience) so a multi-day experience doesn't
 * share them across its days.
 */
export function useExperienceNestedField(
  dayIndex: number,
  fieldName: string,
): TripDataFieldTuple<any> {
  const day = useDay(dayIndex);
  const experiences = useTripStore((state) => state.experiences);
  const updateDay = useTripStore((state) => state.updateDay);
  const setExperience = useTripStore((state) => state.setExperience);

  const isDayField =
    fieldName === 'selectedExtras' || fieldName === 'priceLoading';
  const rawValue = day?.[fieldName];
  const dayValue =
    fieldName === 'selectedExtras'
      ? Array.isArray(rawValue)
        ? rawValue
        : []
      : rawValue;

  const experienceId = day?.experienceId;
  const experienceValue = experienceId
    ? experiences?.[experienceId]?.[fieldName]
    : undefined;

  const setDayValue = useCallback(
    (newValue: any) => {
      const safeValue =
        fieldName === 'selectedExtras'
          ? Array.isArray(newValue)
            ? newValue
            : []
          : newValue;
      updateDay(dayIndex, { [fieldName]: safeValue });
    },
    [updateDay, dayIndex, fieldName],
  );

  const setExperienceValue = useCallback(
    (newValue: any) => {
      if (experienceId) {
        // Non-reactive, imperative read — the experience *at the time of
        // the call*, not the one from the render that created this closure.
        const currentExperience =
          useTripStore.getState().experiences?.[experienceId];
        if (currentExperience) {
          setExperience(experienceId, {
            ...currentExperience,
            [fieldName]: newValue,
          });
        }
      }
    },
    [experienceId, setExperience, fieldName],
  );

  if (isDayField) {
    return [{ value: dayValue }, null, { setValue: setDayValue }];
  }

  return [{ value: experienceValue }, null, { setValue: setExperienceValue }];
}
